import { AttractionTierEnum } from './attraction-layers';

/**
 * Stored place progress. Computed when the place changes and read by the
 * attractions list, so a page of rows never loads the fact bank.
 *
 * Levels follow the content layers. A level counts only when every earlier level is
 * complete. Light and silent places stop early; the snapshot says when the target is met.
 */
export enum AttractionReadinessLevelEnum {
    /** Not triaged yet. */
    NONE = 'none',
    /** Kept by triage, has coordinates and a rough interest score. */
    TRIAGED = 'triaged',
    /** Photo, hours/price/rules checked, tags and interest scores. */
    PASSPORT = 'passport',
    /** Cheap scout recorded story potential. */
    SCOUTED = 'scouted',
    /** Approved short card from scout facts, no audio. */
    LIGHT = 'light',
    /** Full fact bank. */
    BASE = 'base',
    /** Approved story brief. */
    BRIEF = 'brief',
    /** Guide text, emoji cards, hours and visit details. */
    GUIDE = 'guide',
    /** Base-language audio guide. */
    AUDIO = 'audio',
}

export const ATTRACTION_READINESS_CHECK_KEYS = [
    'triage',
    'coordinates',
    'photos',
    'passport',
    'interest',
    'scout',
    'lightCard',
    'factBank',
    'brief',
    'guideTalk',
    'emojiFacts',
    'hours',
    'visitDetails',
    'audio',
] as const;

export type AttractionReadinessCheckKey = (typeof ATTRACTION_READINESS_CHECK_KEYS)[number];

export type AttractionReadinessSnapshot = {
    level: AttractionReadinessLevelEnum;
    checks: Record<AttractionReadinessCheckKey, boolean>;
    /** Chosen depth; null until scouting or route planning decides. */
    tier?: AttractionTierEnum | null;
    /** Level this place should reach; null while the tier is open. */
    target?: AttractionReadinessLevelEnum | null;
    updatedAt: string;
};

type Layer = {
    level: AttractionReadinessLevelEnum;
    checks: readonly AttractionReadinessCheckKey[];
};

const COMMON_LAYERS: readonly Layer[] = [
    { level: AttractionReadinessLevelEnum.TRIAGED, checks: ['triage', 'coordinates'] },
    { level: AttractionReadinessLevelEnum.PASSPORT, checks: ['photos', 'passport', 'interest'] },
    { level: AttractionReadinessLevelEnum.SCOUTED, checks: ['scout'] },
];

const LIGHT_LAYERS: readonly Layer[] = [
    ...COMMON_LAYERS,
    { level: AttractionReadinessLevelEnum.LIGHT, checks: ['lightCard'] },
];

const FULL_LAYERS: readonly Layer[] = [
    ...COMMON_LAYERS,
    { level: AttractionReadinessLevelEnum.BASE, checks: ['factBank'] },
    { level: AttractionReadinessLevelEnum.BRIEF, checks: ['brief'] },
    {
        level: AttractionReadinessLevelEnum.GUIDE,
        checks: ['guideTalk', 'emojiFacts', 'hours', 'visitDetails'],
    },
    { level: AttractionReadinessLevelEnum.AUDIO, checks: ['audio'] },
];

/** Layers in order for a tier. The level is the last one whose checks all pass. */
export const attractionReadinessLayers = (tier?: AttractionTierEnum | null): readonly Layer[] =>
    tier === AttractionTierEnum.LIGHT ? LIGHT_LAYERS : FULL_LAYERS;

/** Kept for callers that only know the full audio path. */
export const ATTRACTION_READINESS_LAYERS = FULL_LAYERS;

export const attractionReadinessTarget = (
    tier?: AttractionTierEnum | null,
): AttractionReadinessLevelEnum | null => {
    if (tier === AttractionTierEnum.ROUTE || tier === AttractionTierEnum.FULL) {
        return AttractionReadinessLevelEnum.AUDIO;
    }
    if (tier === AttractionTierEnum.LIGHT) return AttractionReadinessLevelEnum.LIGHT;
    if (tier === AttractionTierEnum.SILENT) return AttractionReadinessLevelEnum.PASSPORT;

    return null;
};

/** Checks that still block the next layer up to the target. Empty when the target is met. */
export const attractionReadinessGaps = (
    snapshot: AttractionReadinessSnapshot,
): AttractionReadinessCheckKey[] => {
    for (const layer of attractionReadinessLayers(snapshot.tier)) {
        const missing = layer.checks.filter((key) => !snapshot.checks[key]);

        if (missing.length) return [...missing];
        if (snapshot.target === layer.level) return [];
    }

    return [];
};
